/**
 * Development-only logging utility
 *
 * Replaces console.log with production-safe logging.
 * In production, logs are silently ignored.
 *
 * Usage:
 *   import { devLog, devWarn, devError } from '@/utils/devLog';
 *   devLog('Component mounted', { chartId });
 *   devWarn('Deprecated API usage');
 *   devError('Failed to render', error);
 *
 * Migration from console.log:
 *   // Before: console.log('🔄 Rendering:', data);
 *   // After:  devLog('🔄 Rendering:', data);
 */

const isDev = process.env.NODE_ENV === 'development';

/**
 * Log filter - only show logs matching these patterns
 * Set to null to show all logs, or array of patterns to filter
 */
const LOG_FILTER: string[] | null = ['Performance GC'];

/**
 * Check if log message matches any allowed pattern
 */
const matchesFilter = (args: unknown[]): boolean => {
  if (!LOG_FILTER) return true;
  const message = args.map(a => String(a)).join(' ');
  return LOG_FILTER.some(pattern => message.includes(pattern));
};

/**
 * Development-only console.log
 * Silently ignored in production
 * Filtered by LOG_FILTER patterns
 */
export const devLog = (...args: unknown[]): void => {
  if (isDev && matchesFilter(args)) {
    console.log(...args);
  }
};

/**
 * Development-only console.warn
 * Silently ignored in production
 * Filtered by LOG_FILTER patterns
 */
export const devWarn = (...args: unknown[]): void => {
  if (isDev && matchesFilter(args)) {
    console.warn(...args);
  }
};

/**
 * Development-only console.error
 * NOTE: In production, errors are still logged for debugging
 */
export const devError = (...args: unknown[]): void => {
  // Always log errors - they're important for production debugging
  console.error(...args);
};

/**
 * Conditional log based on debug flag
 * Useful for per-component debug toggles
 */
export const debugLog = (enabled: boolean, ...args: unknown[]): void => {
  if (isDev && enabled) {
    console.log(...args);
  }
};

/**
 * Performance timing log
 * Only logs in development, includes timing
 */
export const perfLog = (label: string, startTime: number): void => {
  if (isDev) {
    const duration = performance.now() - startTime;
    console.log(`⏱️ ${label}: ${duration.toFixed(2)}ms`);
  }
};

/**
 * Group logs under collapsible header (dev only)
 */
export const devGroup = (label: string, fn: () => void): void => {
  if (isDev) {
    console.group(label);
    fn();
    console.groupEnd();
  }
};

/**
 * Collapsed group logs (dev only)
 */
export const devGroupCollapsed = (label: string, fn: () => void): void => {
  if (isDev) {
    console.groupCollapsed(label);
    fn();
    console.groupEnd();
  }
};

export default {
  log: devLog,
  warn: devWarn,
  error: devError,
  debug: debugLog,
  perf: perfLog,
  group: devGroup,
  groupCollapsed: devGroupCollapsed,
};
